import React, {type ReactNode} from 'react'
import {CardEnum, type CardStateOrdered, type CardTypeEnum} from '@colonist/game-states'
import {CardDataController} from '@colonist/game-card-types'
import {type ModeSettingEnum} from '@colonist/game-base-types'
import {type TranslationObject} from '@colonist/i18n'
import {T} from '@colonist/ui'
import {CARD_TIP_BODY, CARD_TIP_TITLE} from '@colonist/ui-game'
import {UIGameProgressCardTooltipBody} from '../../common/UIGameProgressCardTooltipBody/UIGameProgressCardTooltipBody.tsx'
import {getBankCardStackCountRange} from './getBankCardStackSize.ts'

export interface BankCardTooltipResult {
    title?: ReactNode
    content?: ReactNode
    centerText?: boolean
}

function getExtraTooltipBody(modeSetting: ModeSettingEnum, cardType: CardTypeEnum, cardCount: number): TranslationObject | undefined {
    if(cardCount === 0) return {key: 'strings:game.tooltips.cardCountInStack.empty'}

    const {lowerBound, upperBound} = getBankCardStackCountRange(modeSetting, cardType, cardCount)
    return {
        key: 'strings:game.tooltips.cardCountInStack.full',
        options: {lowerBound: lowerBound, upperBound: upperBound},
    }
}

export function getBankCardTooltip(cardEnum: CardEnum,
                                   cardCount: number,
                                   hideBankCards: boolean,
                                   modeSetting: ModeSettingEnum,
                                   progressCards: CardStateOrdered | undefined,
                                   translationKeyContext: string,): BankCardTooltipResult {
    const cardData = CardDataController.getCardDataForCard(cardEnum)

    const tipTitle: TranslationObject = {
        ...CARD_TIP_TITLE[cardEnum],
        options: {context: translationKeyContext},
    }
    const tipBody: TranslationObject = {
        ...CARD_TIP_BODY[cardEnum],
        options: {context: translationKeyContext},
    }

    const title = cardData ? <T id={tipTitle}/> : undefined

    const isProgressCard = cardEnum === CardEnum.ProgressCardBack
    if(isProgressCard && progressCards) {
        return {
            title: title,
            content: <UIGameProgressCardTooltipBody
                hideBadge={hideBankCards}
                progressCards={progressCards}
                modeSetting={modeSetting}
            />,
            centerText: true,
        }
    }

    const extraTooltipBody = (hideBankCards && cardData)
        ? getExtraTooltipBody(modeSetting, cardData.cardTypeEnum, cardCount)
        : undefined

    const content = (
        <>
            {cardData && <T id={tipBody}/>}
            {extraTooltipBody !== undefined && (
                <>
                    <br />
                    <br />
                    <T id={extraTooltipBody}/>
                </>
            )}
        </>
    )

    return {
        title: title,
        content: content,
    }
}
